import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import ButtonWithIcon from '../ButtonWithIcon/ButtonWithIcon';
import Heading from '../Heading/Heading';
import Paragraph from '../Paragraph/Paragraph';
import CardUploadSlots from '../cardUpload/CardUploadSlots';
import { compressImage } from '../../utils/imageCompression';

const emptySlots = () => [null, null];

const previewsFromFiles = (files) =>
    emptySlots().map((_, index) => (files[index] ? URL.createObjectURL(files[index]) : null));

/** Only a preview we made ourselves can be released; a stored URL belongs to the backend. */
const revokePreview = (url) => {
    if (typeof url === 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url);
};

const MedicareUpload = ({
    SetMedicareImages,
    files = [],
    error = '',
    storedUrls = [],
    onRemoveStored,
}) => {
    const [selectedFiles, setSelectedFiles] = useState(() => [
        files[0] || null,
        files[1] || null,
    ]);
    const [filePreviews, setFilePreviews] = useState(() => previewsFromFiles(files));
    const [open, setOpen] = useState(false);
    const dialogTimeoutRef = useRef(null);

    useEffect(() => {
        const nextFiles = [files[0] || null, files[1] || null];
        const sameFiles = nextFiles.every((file, index) => file === selectedFiles[index]);
        if (sameFiles) return;

        filePreviews.forEach(revokePreview);
        setSelectedFiles(nextFiles);
        setFilePreviews(previewsFromFiles(nextFiles));
    }, [files]);

    const handleClose = () => {
        setOpen(false);
        if (dialogTimeoutRef.current) {
            clearTimeout(dialogTimeoutRef.current);
            dialogTimeoutRef.current = null;
        }
    };

    const showDialogWithDelay = () => {
        if (dialogTimeoutRef.current) clearTimeout(dialogTimeoutRef.current);
        dialogTimeoutRef.current = setTimeout(() => {
            setOpen(true);
            dialogTimeoutRef.current = null;
        }, 100);
    };

    const handleSelect = async (slotIndex, chosenFile) => {
        const isImage = ['image/png', 'image/jpeg'].includes(chosenFile.type);
        const isDuplicate = selectedFiles.some(
            (file) => file && file.name === chosenFile.name && file.size === chosenFile.size
        );

        if (!isImage || isDuplicate) {
            showDialogWithDelay();
            return;
        }

        // Shrink before anything else sees it, so the preview, the size checks and the upload all
        // work with the smaller file. A card photographed on a phone is several megabytes and only
        // has to be readable.
        const newFile = await compressImage(chosenFile);

        revokePreview(filePreviews[slotIndex]);

        const updatedFiles = [...selectedFiles];
        const updatedPreviews = [...filePreviews];
        updatedFiles[slotIndex] = newFile;
        updatedPreviews[slotIndex] = URL.createObjectURL(newFile);

        setSelectedFiles(updatedFiles);
        setFilePreviews(updatedPreviews);
        SetMedicareImages(updatedFiles);
    };

    const handleRemoveImage = (indexToRemove) => {
        // Nothing was chosen this visit, so what is on screen is the card already on file.
        // Removing it means the parent wants to replace it, which the next upload does.
        if (!selectedFiles[indexToRemove] && storedUrls[indexToRemove]) {
            onRemoveStored?.(indexToRemove);
            return;
        }

        revokePreview(filePreviews[indexToRemove]);

        const updatedFiles = [...selectedFiles];
        const updatedPreviews = [...filePreviews];
        updatedFiles[indexToRemove] = null;
        updatedPreviews[indexToRemove] = null;

        setSelectedFiles(updatedFiles);
        setFilePreviews(updatedPreviews);
        SetMedicareImages(updatedFiles);
    };

    useEffect(() => {
        return () => {
            filePreviews.forEach(revokePreview);
        };
    }, []);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="invalid-file-title"
                aria-describedby="invalid-file-description"
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: '#eee',
                        padding: '25px',
                        borderRadius: '5px',
                    },
                }}
            >
                <Heading id="invalid-file-title" text={'Invalid File Selection'} />
                <DialogContent id="invalid-file-description" sx={{ padding: '0px' }}>
                    <Paragraph text={'Exactly 2 unique JPG or PNG images are required. Duplicate or unsupported files are not allowed.'} />
                </DialogContent>
                <DialogActions>
                    <ButtonWithIcon variant="outlined" text="Close" startIcon={<CloseIcon />} onClick={handleClose} />
                </DialogActions>
            </Dialog>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                <CardUploadSlots
                    filePreviews={filePreviews.map((preview, index) => preview || storedUrls[index] || null)}
                    placeholderSrc="images/MedicareSample.png"
                    placeholderAlt="Medicare card sample"
                    onSelect={handleSelect}
                    onRemove={handleRemoveImage}
                    error={Boolean(error)}
                />
                {error && (
                    <Paragraph color="red" fontSize="13px" text={error} />
                )}
            </Box>
        </>
    );
};

export default MedicareUpload;
